import { useState } from 'react';
import { useTranslation } from '@frontend/i18n';
import {
  ConfirmationModal as UnsavedTaskConfirmationModal,
  ConfirmationModalProps,
  useModalControl,
} from '@frontend/design-system';
import { useTaskCenterStore } from '../provider/task-center.store';

export interface useUnsavedTaskModalProps {
  onConfirm: () => void;
  hasProgress: boolean;
  trackingIds?: {
    unsavedCancelButton: string;
    unsavedConfirmButton: string;
    unsavedBackButton: string;
  };
}

export const useUnsavedTaskModal = ({ onConfirm, trackingIds, hasProgress }: useUnsavedTaskModalProps) => {
  const { t } = useTranslation('task-center');
  const { openModal, modalProps, closeModal } = useModalControl();
  const { setIsTaskCenterOpen } = useTaskCenterStore(['setIsTaskCenterOpen']);
  const [shouldClosePanel, setShouldClosePanel] = useState(false);

  const unsavedTaskTriggerProps = {
    onClick: () => {
      if (hasProgress) {
        openModal();
      } else {
        if (shouldClosePanel) {
          setIsTaskCenterOpen(false);
        }
        onConfirm();
      }
    },
  };

  const confirmationModalProps: ConfirmationModalProps = {
    ...modalProps,
    title: t('Unsaved Changes'),
    message: t('You have unsaved changes. Are you sure you want to leave without saving?'),
    destructive: true,
    confirmTrackingId: trackingIds?.unsavedConfirmButton,
    cancelTrackingId: trackingIds?.unsavedCancelButton,
    backTrackingId: trackingIds?.unsavedBackButton,
    onConfirm: () => {
      if (shouldClosePanel) {
        setIsTaskCenterOpen(false);
      }
      onConfirm();
    },
    onCancel: () => {
      setShouldClosePanel(false);
      closeModal();
    },
    onBackClick: () => {
      setShouldClosePanel(false);
      closeModal();
    },
  };

  return {
    UnsavedTaskConfirmationModal,
    unsavedTaskModalProps: confirmationModalProps,
    unsavedTaskTriggerProps,
    shouldClosePanel,
    setShouldClosePanel,
  };
};
